import type { ModifierDef } from '../effects/types';

/*
 * Outta-Ten content (ADR 0066, CONTRACT §1-§2): shared-cs Skills/SkillIds.cs, Skills/PerkFlags.cs and Content/SkillDefs.cs. The ids are
 * code (a new stat or skill is a code change in shared-cs and here); content tunes the numbers. Typed locally until the API client is
 * regenerated from the spec (OT-C). Every field is optional: an absent one is its C# default (`lib/skills/defaults.ts`).
 */

/** shared-cs `SheetKinds`. */
export const SHEET_KINDS = ['stat', 'skill'] as const;

/** shared-cs `StatIds.All`, in sheet order, with `Name` and `Short`. */
export const CORE_STAT_IDS = ['str', 'end', 'ref', 'wit', 'nrv', 'cha'] as const;
export const CORE_STAT_NAMES: Record<string, string> = {
  str: 'Strength',
  end: 'Endurance',
  ref: 'Reflex',
  wit: 'Wits',
  nrv: 'Nerve',
  cha: 'Charm',
};
/** `StatIds.Legacy`: ADR 0044's ids and the new id each became (the validator's "is now" hint). */
export const LEGACY_STAT_IDS: Record<string, string> = {
  brawn: 'str',
  aim: 'ref',
  stealth: 'ref',
  wits: 'wit',
  care: 'wit',
  nerve: 'nrv',
};

/** shared-cs `SkillFamilies.All`. */
export const SKILL_FAMILIES = ['weapon', 'field', 'home'] as const;
/** shared-cs `SkillIds` by family (the launch skills; `SkillIds.All` is their concatenation in this order). */
export const SKILL_IDS_BY_FAMILY: Record<(typeof SKILL_FAMILIES)[number], readonly string[]> = {
  weapon: ['blunt', 'blades', 'pistols', 'shotguns', 'rifles', 'bows', 'automatics', 'throwing'],
  field: ['stealth', 'scavenging', 'breaching', 'first_aid', 'driving'],
  home: ['construction', 'tinkering', 'medicine'],
};
export const SKILL_IDS: readonly string[] = [
  ...SKILL_IDS_BY_FAMILY.weapon,
  ...SKILL_IDS_BY_FAMILY.field,
  ...SKILL_IDS_BY_FAMILY.home,
];
/** `SkillIds.FamilyOf`. */
export const skillFamilyOf = (id: string | null | undefined): string | null => {
  for (const f of SKILL_FAMILIES) if (SKILL_IDS_BY_FAMILY[f].includes(id ?? '')) return f;
  return null;
};

/** shared-cs `SheetPoints` and `Milestones`. */
export const SHEET_POINTS_MAX = 1000;
export const MILESTONE_ABILITY_AT = 5;
/** The perk at 7 (`Milestones.PerkAt`; CONTRACT §13.4: it arrives, no pick). */
export const MILESTONE_PERK_AT = 7;
export const MILESTONE_SIGNATURE_AT = 10;

/** shared-cs `SkillEffectIds.Families`: the derived-effect families (`effectRules.derivedFamilies`). */
export const DERIVED_FAMILIES = ['skill', 'stat', 'perk', 'milestone'] as const;
/** shared-cs `SkillEffectIds.AllFamilies`: every family a pseudo id may name, `health` (ADR 0073, `HealthLevelProvider`) included. */
export const ALL_DERIVED_FAMILIES = [...DERIVED_FAMILIES, 'health'] as const;
/** `SkillEffectIds.FamilyOf`: the family of a pseudo id `skill:blunt`, `perk:x`, `milestone:stat:str:10`, or null. */
export const derivedFamilyOf = (effectId: string | null | undefined): string | null => {
  const id = effectId ?? '';
  const c = id.indexOf(':');
  if (c <= 0) return null;
  const f = id.slice(0, c);
  return (ALL_DERIVED_FAMILIES as readonly string[]).includes(f) ? f : null;
};

/** shared-cs `ActivityCategories.All` (health intake): the workout categories. */
export const ACTIVITY_CATEGORIES = [
  'walk',
  'run',
  'cycle',
  'hike',
  'strength',
  'hiit',
  'swim',
  'sport',
  'other',
] as const;

/** shared-cs `PerkFlags.All`: the behaviours a milestone or perk may switch on (in the C# order). */
export const PERK_FLAGS = [
  'shove',
  'softFeet',
  'silentKill',
  'bandageClearsWounded',
  'homeRun',
  'followThrough',
  'ironGrip',
  'quietWork',
  'bleeder',
  'quickDraw',
  'butcher',
  'doubleTap',
  'sidearm',
  'deadEye',
  'pumpRhythm',
  'scattergun',
  'doorman',
  'marksman',
  'breathHold',
  'pierce',
  'oneShot',
  'fletcher',
  'snapshot',
  'whisper',
  'shortBursts',
  'beltFed',
  'hipFire',
  'suppressor',
  'cookIt',
  'twoUp',
  'pinpoint',
  'shadow',
  'ghost',
  'packrat',
  'nose',
  'locksmith',
  'keymaster',
  'reviveHalfHp',
  'lifeline',
  'handbrake',
  'roadDog',
  'ramPlate',
  'stunt',
  'scrapSaver',
  'foreman',
  'repairCostLess',
  'fineWork',
  'salvager',
  'masterwork',
  'quarantine',
  'doctor',
  'secondWind',
  'coolHead',
] as const;

/** shared-cs `GovernDef`: `neutral op perLevel x (level - pivot)`, or `table[level]` (11 values) when the curve isn't linear. */
export type GovernDef = { key?: string; perLevel?: number; table?: number[] | null };
/** shared-cs `MilestoneDef`: `ability5` / `signature10`, live while the level is at least 5 / 10. */
export type MilestoneDef = {
  name?: string;
  modifiers?: (ModifierDef | null)[] | null;
  flags?: string[] | null;
  text?: string;
};
/** shared-cs `CoreStatDef` (`coreStats[]`, six rows). */
export type CoreStatDef = {
  id?: string;
  name?: string;
  short?: string;
  text?: string;
  learnSpeed?: number;
  rustClass?: string;
  governs?: (GovernDef | null)[] | null;
  ability5?: MilestoneDef | null;
  /** The perk that arrives at 7 (CONTRACT §13.4): a `perks[]` id this row owns; active while the level is 7 or more. Required. */
  ability7?: string | null;
  signature10?: MilestoneDef | null;
  /** What it affects, for the player's info row (CONTRACT §13.2; 20..240 characters). */
  affectsText?: string | null;
  /** How to raise it, for the player's info row (CONTRACT §13.2; 20..240 characters). */
  trainText?: string | null;
};
/** shared-cs `SkillDef` (`skills[]`). */
export type SkillDef = Omit<CoreStatDef, 'short'> & {
  family?: string;
  baseline?: number;
  cantBelow?: number;
};
/** shared-cs `PerkDef` (`perks[]`): the perk definitions, owned by `stat:<id>` or `skill:<id>`; a row's `ability7` names the one that arrives at 7. */
export type PerkDef = {
  id?: string;
  owner?: string;
  name?: string;
  text?: string;
  modifiers?: (ModifierDef | null)[] | null;
  flags?: string[] | null;
};
export type RustClassDef = {
  perDay?: number[] | null;
  floorPoints?: number;
  belowPeakPoints?: number;
};
export type StatAwardsDef = {
  meleeKillStr?: number;
  haulStr?: number;
  haulKgPerMember?: number;
  extractedEnd?: number;
  waveEnd?: number;
  rangedKillRef?: number;
  stealthKillRef?: number;
  reviveRef?: number;
  searchWit?: number;
  buildWit?: number;
  waveNrv?: number;
  bigWinNrv?: number;
  revivedNrv?: number;
  recruitCha?: number;
  tradeCha?: number;
};
export type AwardsDef = {
  kill?: number;
  headshot?: number;
  stealthKill?: number;
  search?: number;
  forcedDoor?: number;
  lockPicked?: number;
  treat?: number;
  revive?: number;
  throwLanded?: number;
  bigWin?: number;
  homeBuild?: number;
  homeDischarge?: number;
  stat?: StatAwardsDef | null;
};
export type ChallengesDef = {
  door?: number;
  lock?: number;
  revive?: number;
  treat?: number;
  search?: number;
};
export type WorkoutWeightsDef = {
  endPerCreditedMin?: Record<string, number> | null;
  strPerCreditedMin?: Record<string, number> | null;
  meleePerCreditedMin?: Record<string, number> | null;
  meleeSkills?: string[] | null;
  meleeMaxLevel?: number;
};
export type StartDef = { playerSwaps?: number; recruitSwaps?: number; swapPoints?: number };
/** shared-cs `SkillRulesDef` (`skillRules`). */
export type SkillRulesDef = {
  enabled?: boolean;
  rate?: number[] | null;
  dailyCapPoints?: number;
  homeDailyCapPoints?: number;
  challengeFloor?: number;
  challengeStepPerLevel?: number;
  muscleMemoryMult?: number;
  graceDays?: number;
  deathRustDays?: number;
  rustClasses?: Record<string, RustClassDef | null> | null;
  awards?: AwardsDef | null;
  challenges?: ChallengesDef | null;
  workouts?: WorkoutWeightsDef | null;
  start?: StartDef | null;
  homeSkillLearnPerWit?: number;
  reducedShare?: number;
  reducedConditions?: string[] | null;
  noShareConditions?: string[] | null;
  devPointsMult?: number;
  signatureHeadshot?: number;
  reloadMinS?: number;
  knockbackMaxM?: number;
  maxBonusLootRolls?: number;
};
/** shared-cs `StaminaDef` (`raidRules.stamina`). */
export type StaminaDef = {
  baseS?: number;
  regenPerS?: number;
  windedS?: number;
  windedSpeedMult?: number;
  windedSpreadMult?: number;
  windedAcquireMult?: number;
};
/** shared-cs `SkillRequirementDef` (reserved on recipes and module levels; nothing gates in v1). */
export type SkillRequirementDef = { id?: string; level?: number };
/** shared-cs `BackgroundDef` (`backgrounds[]`): bias keys are `stat:<id>` / `skill:<id>`. */
export type BackgroundDef = {
  id?: string;
  bias?: Record<string, number> | null;
  jacket?: string;
  weight?: number;
  legacyRole?: string;
};

/** `SheetOwners.TryParse`: `stat:str` gives ['stat', 'str']; null when it isn't a well-formed owner. */
export function parseOwner(owner: string | null | undefined): [string, string] | null {
  const o = owner ?? '';
  const c = o.indexOf(':');
  if (c <= 0 || c === o.length - 1) return null;
  const kind = o.slice(0, c);
  const id = o.slice(c + 1);
  return (SHEET_KINDS as readonly string[]).includes(kind) && !id.includes(':') ? [kind, id] : null;
}
